import * as React from 'react';
import { observer } from 'src/utils/mobx-react';

import { MainLayout } from './uiComponents/mainLayout';
import { EnvironmentState } from 'src/domains/layouts/state/environmentState/EnvironmentState';
import { Overlay } from 'src/domains/layouts/webview/components/overlay/Overlay';
import { QuickBet } from 'src/domains/sportsbook/betting/ui/betSlip/quickBet/QuickBet';
import { AppWrapper } from './ui.style';
import { NoConnection } from 'src/domains/layouts/layouts/utilityPages/errorPage/NoConnection';
import { SrgQuestionnaire } from 'src/domains/players/state/srgQuestionnaire/srgQuestionnaire/SrgQuestionnaire';
import { TraderChatButtonFloating } from 'src/domains/players/webview/components/Account/accountPanel/accountNavigation/traderChatLinkNav/TraderChatLinkNav';
import { FreshDeskIframe } from 'src/domains/layouts/webview/components/freshDesk/FreshDeskIframe';
import { PofPopups } from 'src/domains/players/webview/components/PofPopups';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { useCommon } from 'src/domains/common/Common';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { TraderChatState } from 'src/domains/players/state/traderChat/TraderChatState';
import { BlockingNotifications } from 'src/domains/players/webview/components/Account/Notifications/BlockingNotifications';
import { IntercomChat } from 'src/domains/layouts/webview/components/IntercomChat';
import { IdMission } from 'src/domains/players/state/IdMission/IdMissionPopup/IdMissionPopup';
import { StaticPageContent } from 'src/domains/players/webview/components/Account/Notifications/StaticPageContent';
import { UsersState } from 'src/domains/players/state/UsersState';
import {
    BlockingNotificationType,
    NonBlockingNotificationType,
} from 'src_server/trpc/types/accountModel/accountResponse';
import { NonBlockingNotifications } from 'src/domains/players/webview/components/Account/Notifications/NonBlockingNotifications';
import { SdkCustomer } from 'src/domains/layouts/state/customer';
import { StreamingState } from 'src/domains/sportsbook/state/streamingState/StreamingState';
import { BlockingNotificationsState } from 'src/domains/players/state/BlockingNotification.state';
import { NonBlockingNotificationsState } from 'src/domains/players/state/NonBlockingNotification.state';
import { IdentityState } from 'src/domains/players/state/IdMission/IdentityState';
import { Overlay as OverlayState } from '../state/overlay/Overlay';
import { BannersBoxState } from '../state/bannersBoxState/BannersBoxState';
import { NetworkConnectionState } from '../state/networkConnectionState/NetworkConnectionState';
import { GamblingSafetyBar } from './uiComponents/GamblingSafetyBar';
import { PopupsWithStartApp } from './uiComponents/PopupsWithStartApp';
import { OptimoveHost } from './uiComponents/OptimoveHost';
import { PopupState } from '../state/popupState/PopupState';
import { RacingStreamGlobal } from 'src/domains/sportsbook/webview/components/stream/RacingStreamGlobal';
import { PopupContainer } from 'src/domains/layouts/webview/components/popups/PopupContainer';
import { BannersMain } from './uiComponents/BannersMain';
import { CasinoMiniMain } from './uiComponents/CasinoMiniMain';

export const AppInner = observer('AppInner', () => {
    const common = useCommon();
    const config = ConfigComponents.get(common).config;
    const router = StarRouter.get(common);
    const env = EnvironmentState.get(common);
    const { popupForView } = PopupState.get(common);

    const { srgQuestionnaire: isSrgQuestionnaire, traderChat, freshDeskChat, intercomChat } = config;

    const blockingNotificationsState = BlockingNotificationsState.get(common);
    const nonBlockingNotificationsState = NonBlockingNotificationsState.get(common);

    const usersState = UsersState.get(common);
    const isMobileApp = env.isMobileApp();

    const getMainId = (): string | undefined => {
        return 'app-wrapper';
    };

    const sdkCustomer = SdkCustomer.get(common);
    const { userId } = sdkCustomer.session;

    const overlayOnClick = (): void => {
        //Handler for overlay - to improve in the future
        // console.info('overlay on click');
        OverlayState.get(common).toggleMenu();
    };

    const name = router.currentView?.name ?? null;
    const isAnyCasinoPage = name === 'casino' || name === 'virtuals' || name === 'live-casino';
    const isRaceCard = name === 'racecard';
    const currentView = router.currentView;
    const accountView = router.accountView;

    const isNotificationsList =
        router.currentView?.name === 'notifications-list' || accountView?.account === 'terms-and-conditions-promos';

    const { streamBoxHeight, isStreamFloating, isStreamOpen } = StreamingState.get(common);
    const { boxHeight: bannersHeight } = BannersBoxState.get(common);
    const { shouldOverlayOpen, overlayLevel: overlayIndex } = OverlayState.get(common);

    const traderChatState = TraderChatState.get(common);
    const hasIdMission = ConfigComponents.get(common).config.idMission;
    const { pofModals } = ConfigComponents.get(common).config;
    const isAvailableBubbleChatButton = traderChat.isBubbleChatButton && traderChatState.isTraderChatForView;

    const { showOfflineMessage } = NetworkConnectionState.get(common);

    const blockingNotificationType = blockingNotificationsState.getBlockingNotificationType;
    const nonBlockingNotificationType = nonBlockingNotificationsState.getNonBlockingNotificationType;

    const isKycRestrictionWithIdMission =
        hasIdMission && blockingNotificationType === BlockingNotificationType.KYC_DEPOSIT_RESTRICTION;

    if (blockingNotificationType !== null && isKycRestrictionWithIdMission === false) {
        return <BlockingNotifications blockingNotificationType={blockingNotificationType} />;
    }

    if (currentView?.name === 'homepage' && currentView.staticPage !== null) {
        if (currentView.staticPage === 'terms-and-conditions' || currentView.staticPage === 'privacy-policy') {
            return <StaticPageContent id={currentView.staticPage} />;
        }
    }

    const identityStateProcessIdMissionIsOpen = IdentityState.get(common).processIdMission.isOpen;

    return (
        <AppWrapper
            data-track={userId}
            id={getMainId()}
            bannersHeight={bannersHeight}
            streamHeight={streamBoxHeight}
            isStreamFloating={isStreamFloating}
            isLayoutHasAside={true}
        >
            {showOfflineMessage && isMobileApp === false ? <NoConnection /> : null}
            {isStreamOpen ? (
                <RacingStreamGlobal
                    isRaceCard={isRaceCard}
                    isAnyCasinoPage={isAnyCasinoPage}
                    isNotificationsList={isNotificationsList}
                />
            ) : null}
            <GamblingSafetyBar />
            <BannersMain />
            <MainLayout />
            <PopupsWithStartApp />
            <QuickBet />
            {popupForView === null ? null : <PopupContainer />}

            {nonBlockingNotificationType === null ? null : (
                <NonBlockingNotifications nonBlockingNotificationType={NonBlockingNotificationType.REVIEW_ACCOUNT} />
            )}

            {usersState.isSrgModalOpen && isSrgQuestionnaire ? <SrgQuestionnaire /> : null}
            {hasIdMission && identityStateProcessIdMissionIsOpen === true ? <IdMission /> : null}
            {isAvailableBubbleChatButton ? <TraderChatButtonFloating /> : null}
            {freshDeskChat === null ? null : <FreshDeskIframe />}
            <CasinoMiniMain />
            <Overlay
                isVisible={shouldOverlayOpen}
                onClick={overlayOnClick}
                zIndex={overlayIndex}
            />
            {pofModals ? <PofPopups /> : null}
            {intercomChat === true ? <IntercomChat /> : null}
            <OptimoveHost />
        </AppWrapper>
    );
});
